/**
 * Admin Page - Auto-Trading Bot Configuration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { botAPI, wsbAPI } from '../services/api';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';
import { useAuth } from '../context/AuthContext';
import { markBotControlVisited } from '../components/Onboarding';
import {
  Bot, Play, Square, Settings, AlertTriangle, TrendingUp,
  TrendingDown, DollarSign, Clock, Target, Shield, Zap, BarChart3,
  RefreshCw, Trash2, Calendar, Activity, CheckCircle, XCircle, Brain,
  MessageCircle, Users, Flame, ExternalLink, Layers
} from 'lucide-react';

// ============ TOGGLE SWITCH ============
const Toggle = ({ enabled, onChange, disabled }) => (
  <button
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    className={`relative w-14 h-7 rounded-full transition-colors ${
      enabled ? 'bg-accent-green' : 'bg-bg-tertiary'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
      enabled ? 'left-8' : 'left-1'
    }`} />
  </button>
);

// ============ INPUT FIELD ============
const InputField = ({ label, type = 'number', value, onChange, min, max, step, suffix, description }) => (
  <div className="mb-4">
    <label className="block text-text-secondary text-sm mb-2">{label}</label>
    <div className="relative">
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
        min={min}
        max={max}
        step={step}
        className="input font-mono pr-12"
      />
      {suffix && (
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted">{suffix}</span>
      )}
    </div>
    {description && <p className="text-xs text-text-muted mt-1">{description}</p>}
  </div>
);

// ============ MINI SPARKLINE ============
const MiniSparkline = ({ trend = 'up', color = 'blue' }) => {
  // Generate simple trend line
  const points = trend === 'up' 
    ? [8, 6, 7, 4, 5, 3, 2] 
    : trend === 'down' 
    ? [2, 3, 4, 3, 5, 6, 8]
    : [4, 5, 3, 6, 4, 5, 4];
  
  const width = 50;
  const height = 20;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  
  const pathPoints = points.map((p, i) => {
    const x = (i / (points.length - 1)) * width;
    const y = height - ((p - min) / range) * height;
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  const colors = {
    blue: '#3B82F6',
    green: '#10B981',
    red: '#EF4444',
    purple: '#8B5CF6',
  };

  return (
    <svg width={width} height={height} className="opacity-60">
      <path d={pathPoints} fill="none" stroke={colors[color]} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
};

// ============ STAT BOX ============
const StatBox = ({ label, value, icon: Icon, color = 'blue', trend }) => {
  const colorClasses = {
    blue: 'bg-accent-blue/15 text-accent-blue',
    green: 'bg-accent-green/15 text-accent-green',
    red: 'bg-accent-red/15 text-accent-red',
    purple: 'bg-accent-purple/15 text-accent-purple',
  };

  return (
    <div className="card p-4 group hover:border-border-hover transition-all">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-text-muted text-xs">{label}</p>
            <p className="font-mono font-semibold text-lg">{value}</p>
          </div>
        </div>
        <MiniSparkline trend={trend} color={color} />
      </div>
    </div>
  );
};

// ============ MAIN ADMIN PAGE ============
const Admin = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [botStatus, setBotStatus] = useState({ isRunning: false, stats: null });
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [wsbTrending, setWsbTrending] = useState([]);
  const [wsbLoading, setWsbLoading] = useState(false);

  // Fetch bot status and config
  const fetchData = useCallback(async () => {
    try {
      const [statusRes, configRes, historyRes] = await Promise.all([
        botAPI.getStatus(),
        botAPI.getConfig(),
        botAPI.getHistory(20),
      ]);
      setBotStatus(statusRes.data);
      setConfig(configRes.data.config);
      setHistory(historyRes.data.trades || []);
    } catch (err) {
      console.error('Failed to fetch bot data:', err);
      toast.error('Failed to load bot settings');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch WSB data
  const fetchWSBData = useCallback(async () => {
    setWsbLoading(true);
    try {
      const res = await wsbAPI.getTrending(10);
      setWsbTrending(res.data.tickers || []);
    } catch (err) {
      console.error('Failed to fetch WSB data:', err);
    } finally {
      setWsbLoading(false);
    }
  }, []);

  // Reaching Bot Control ticks off the "Set bot preferences" onboarding step.
  useEffect(() => { markBotControlVisited(user?.id); }, [user?.id]);

  useEffect(() => {
    fetchData();
    fetchWSBData();
    // Refresh every 30 seconds if bot is running
    const interval = setInterval(() => {
      if (botStatus.isRunning) fetchData();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchData, fetchWSBData, botStatus.isRunning]);

  // Update config
  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  // Save config
  const saveConfig = async () => {
    setSaving(true);
    try {
      await botAPI.updateConfig(config);
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Start bot
  const startBot = async () => {
    try {
      const result = await botAPI.start();
      if (result.data.success) {
        toast.success('Bot started successfully');
        fetchData();
      } else {
        toast.error(result.data.error || 'Failed to start bot');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start bot');
    }
  };

  // Stop bot
  const stopBot = async () => {
    try {
      const result = await botAPI.stop();
      if (result.data.success) {
        toast.success('Bot stopped');
        fetchData();
      } else {
        toast.error(result.data.error || 'Failed to stop bot');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to stop bot');
    }
  };

  // Clear history
  const clearHistory = async () => {
    if (!window.confirm('Are you sure you want to clear all bot history?')) return;
    try {
      await botAPI.clearHistory();
      setHistory([]);
      toast.success('History cleared');
    } catch (err) {
      toast.error('Failed to clear history');
    }
  };

  if (loading || !config) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-3 border-border-color border-t-accent-blue rounded-full animate-spin" />
      </div>
    );
  }

  const stats = botStatus.stats || {};

  return (
    <div className="min-h-screen">
      <Navigation 
        title="Auto-Trading Bot" 
        subtitle="Configure automated trading settings"
        onRefresh={fetchData}
      />
      
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        {/* Bot Control */}
        <div className="flex items-center justify-end mb-6 gap-3">
          {botStatus.isRunning ? (
            <button onClick={stopBot} className="btn-secondary px-6 py-3 text-accent-red border-accent-red/30 hover:bg-accent-red/10 flex items-center gap-2">
              <Square className="w-5 h-5" />
              Stop Bot
            </button>
          ) : (
            <button onClick={startBot} className="btn-primary px-6 py-3 flex items-center gap-2">
              <Play className="w-5 h-5" />
              Start Bot
            </button>
          )}
        </div>

      {/* Status Banner */}
      <div className={`card p-4 mb-6 border-2 ${botStatus.isRunning ? 'border-accent-green bg-accent-green/5' : 'border-border-color'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${botStatus.isRunning ? 'bg-accent-green animate-pulse' : 'bg-text-muted'}`} />
            <span className={`font-semibold ${botStatus.isRunning ? 'text-accent-green' : 'text-text-secondary'}`}>
              {botStatus.isRunning ? 'Bot is Active' : 'Bot is Stopped'}
            </span>
          </div>
          {botStatus.isRunning && stats.lastTradeTime && (
            <span className="text-text-muted text-sm">
              Last trade: {new Date(stats.lastTradeTime).toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      {botStatus.isRunning && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatBox label="Today's Trades" value={stats.todayTrades || 0} icon={Activity} color="blue" trend="up" />
          <StatBox label="Today's P&L" value={`$${(stats.todayPnL || 0).toFixed(2)}`} icon={DollarSign} color={stats.todayPnL >= 0 ? 'green' : 'red'} trend={stats.todayPnL >= 0 ? 'up' : 'down'} />
          <StatBox label="Win Rate" value={`${stats.winRate || 0}%`} icon={Target} color="purple" trend={parseFloat(stats.winRate) >= 50 ? 'up' : 'neutral'} />
          <StatBox label="Volume" value={`$${(stats.todayVolume || 0).toLocaleString()}`} icon={BarChart3} color="blue" trend="up" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Risk Management */}
        <div>
          <div className="card p-6 mb-6">
            <h2 className="font-display text-lg font-semibold mb-6 flex items-center gap-2">
              <Shield className="w-5 h-5 text-accent-red" />
              Risk Management
            </h2>

            <InputField
              label="Risk Per Trade"
              value={config.riskPercentage}
              onChange={(v) => updateConfig('riskPercentage', v)}
              min={0.5}
              max={10}
              step={0.5}
              suffix="%"
              description="Maximum % of portfolio to risk on a single trade"
            />

            <InputField
              label="Max Daily Trades"
              value={config.maxDailyTrades}
              onChange={(v) => updateConfig('maxDailyTrades', v)}
              min={1}
              max={100}
              step={1}
              description="Stop trading after this many trades per day"
            />

            <InputField
              label="Max Daily Loss"
              value={config.maxDailyLoss}
              onChange={(v) => updateConfig('maxDailyLoss', v)}
              min={1}
              max={20}
              step={0.5}
              suffix="%"
              description="Stop trading if daily loss exceeds this %"
            />

            <InputField
              label="Max Daily Profit"
              value={config.maxDailyProfit}
              onChange={(v) => updateConfig('maxDailyProfit', v)}
              min={1}
              max={50}
              step={1}
              suffix="%"
              description="Optional: Stop after reaching profit target"
            />

            <InputField
              label="Max Concurrent Positions"
              value={config.maxPositions}
              onChange={(v) => updateConfig('maxPositions', v)}
              min={1}
              max={50}
              step={1}
              description="Maximum number of open positions at once"
            />

            {/* Stock Search Settings */}
            <div className="mt-6 pt-6 border-t border-border-color">
              <h3 className="text-sm font-semibold text-text-secondary mb-4 flex items-center gap-2">
                🔍 Stock Search Settings
              </h3>
              <div className="space-y-4">
                <InputField
                  label="Stocks to Analyze"
                  value={config.stocksToAnalyze || 50}
                  onChange={(v) => updateConfig('stocksToAnalyze', v)}
                  min={10}
                  max={100}
                  step={5}
                  description="How many stocks to analyze each cycle"
                />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-text-secondary text-xs mb-1">Bad Score Threshold</label>
                    <input
                      type="number"
                      value={config.badScoreThreshold || 35}
                      onChange={(e) => updateConfig('badScoreThreshold', parseInt(e.target.value) || 35)}
                      min={20}
                      max={50}
                      className="input font-mono text-sm py-1.5"
                    />
                    <p className="text-xs text-text-muted mt-1">Skip stocks scoring below this</p>
                  </div>
                  <div>
                    <label className="block text-text-secondary text-xs mb-1">Bad Score Cooldown</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={config.badScoreCooldownMinutes || 120}
                        onChange={(e) => updateConfig('badScoreCooldownMinutes', parseInt(e.target.value) || 120)}
                        min={30}
                        max={480}
                        className="input font-mono text-sm py-1.5 pr-12"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted text-xs">min</span>
                    </div>
                    <p className="text-xs text-text-muted mt-1">How long to skip bad stocks</p>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-accent-blue/10 border border-accent-blue/20">
                  <p className="text-xs text-accent-blue">
                    <strong>How it works:</strong> Bot analyzes {config.stocksToAnalyze || 50} stocks each cycle. 
                    Stocks scoring below {config.badScoreThreshold || 35}% are skipped for {config.badScoreCooldownMinutes || 120} minutes 
                    to avoid re-analyzing poor opportunities.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <h2 className="font-display text-lg font-semibold mb-6 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-accent-green" />
              Trade Sizing
            </h2>

            <div className="mb-4">
              <label className="block text-text-secondary text-sm mb-2">Sizing Method</label>
              <div className="flex gap-2">
                <button
                  onClick={() => updateConfig('tradeSize', 'fixed')}
                  className={`flex-1 py-3 px-4 rounded-lg border transition-all ${
                    config.tradeSize === 'fixed'
                      ? 'bg-accent-blue/15 border-accent-blue text-accent-blue'
                      : 'border-border-color text-text-secondary hover:border-text-muted'
                  }`}
                >
                  Fixed Amount
                </button>
                <button
                  onClick={() => updateConfig('tradeSize', 'percentage')}
                  className={`flex-1 py-3 px-4 rounded-lg border transition-all ${
                    config.tradeSize === 'percentage'
                      ? 'bg-accent-blue/15 border-accent-blue text-accent-blue'
                      : 'border-border-color text-text-secondary hover:border-text-muted'
                  }`}
                >
                  % of Buying Power
                </button>
              </div>
            </div>

            {config.tradeSize === 'fixed' ? (
              <InputField
                label="Fixed Trade Amount"
                value={config.fixedTradeAmount}
                onChange={(v) => updateConfig('fixedTradeAmount', v)}
                min={100}
                max={100000}
                step={100}
                suffix="$"
                description="Dollar amount per trade"
              />
            ) : (
              <InputField
                label="Trade Percentage"
                value={config.percentageTradeAmount}
                onChange={(v) => updateConfig('percentageTradeAmount', v)}
                min={1}
                max={25}
                step={1}
                suffix="%"
                description="% of buying power per trade"
              />
            )}

            <InputField
              label="Stop Loss"
              value={config.stopLossPercent}
              onChange={(v) => updateConfig('stopLossPercent', v)}
              min={0.5}
              max={10}
              step={0.5}
              suffix="%"
              description="Automatic stop loss percentage"
            />

            <InputField
              label="Take Profit"
              value={config.takeProfitPercent}
              onChange={(v) => updateConfig('takeProfitPercent', v)}
              min={1}
              max={50}
              step={1}
              suffix="%"
              description="Automatic take profit percentage"
            />

            {/* Smart Position Sizing */}
            <div className="mt-6 pt-6 border-t border-border-color">
              <h3 className="text-sm font-semibold text-text-secondary mb-2 flex items-center gap-2">
                📐 Smart Position Sizing
              </h3>
              <p className="text-xs text-text-muted mb-4">ATR-based sizing caps your max loss per trade, not just trade size.</p>
              
              <InputField
                label="ATR Stop Multiplier"
                value={config.atrStopMultiplier || 2.0}
                onChange={(v) => updateConfig('atrStopMultiplier', v)}
                min={0.5}
                max={5.0}
                step={0.5}
                suffix="×"
                description="Stop distance = ATR × this value (use optimizer to find best)"
              />
              
              <InputField
                label="Max Risk Per Trade"
                value={config.maxRiskPerTrade || 1.0}
                onChange={(v) => updateConfig('maxRiskPerTrade', v)}
                min={0.25}
                max={5.0}
                step={0.25}
                suffix="%"
                description="Max % of portfolio you can LOSE on one trade"
              />
              
              <InputField
                label="Max Position Size"
                value={config.maxPositionPct || 5.0}
                onChange={(v) => updateConfig('maxPositionPct', v)}
                min={1}
                max={25}
                step={1}
                suffix="%"
                description="Max % of portfolio in a single stock"
              />
              
              <InputField
                label="Min Trade Amount"
                value={config.minTradeAmount || 200}
                onChange={(v) => updateConfig('minTradeAmount', v)}
                min={50}
                max={5000}
                step={50}
                suffix="$"
                description="Skip trades below this dollar amount"
              />

              <div className="flex items-center justify-between mt-3 bg-bg-tertiary/50 rounded-xl p-3">
                <div>
                  <div className="text-sm font-medium">Scale to Capital</div>
                  <div className="text-xs text-text-muted">Auto-size trades based on buying power</div>
                </div>
                <button
                  onClick={() => updateConfig('scaleToCapital', !(config.scaleToCapital !== false))}
                  className={`w-12 h-6 rounded-full transition-colors ${config.scaleToCapital !== false ? 'bg-accent-green' : 'bg-bg-tertiary'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${config.scaleToCapital !== false ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>

            {/* Regulatory Fees Section (Alpaca/SEC/FINRA) */}
            <div className="mt-6 pt-6 border-t border-border-color">
              <h3 className="text-sm font-semibold text-text-secondary mb-2 flex items-center gap-2">
                💰 Regulatory Fees (Stocks & ETFs)
              </h3>
              <p className="text-xs text-text-muted mb-4">Alpaca charges $0 commission. These are regulatory fees on sells only.</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-text-secondary text-xs mb-1">SEC Fee Rate</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={config.fees?.stocks?.secFeeRate || 0.0000278}
                      onChange={(e) => updateConfig('fees', {
                        ...config.fees,
                        stocks: { ...config.fees?.stocks, secFeeRate: parseFloat(e.target.value) || 0 }
                      })}
                      min={0}
                      max={0.001}
                      step={0.0000001}
                      className="input font-mono text-sm py-1.5"
                    />
                  </div>
                  <p className="text-xs text-text-muted mt-1">Per $ (sells only)</p>
                </div>
                <div>
                  <label className="block text-text-secondary text-xs mb-1">FINRA TAF / Share</label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted text-xs">$</span>
                    <input
                      type="number"
                      value={config.fees?.stocks?.finraTafPerShare || 0.000166}
                      onChange={(e) => updateConfig('fees', {
                        ...config.fees,
                        stocks: { ...config.fees?.stocks, finraTafPerShare: parseFloat(e.target.value) || 0 }
                      })}
                      min={0}
                      max={0.01}
                      step={0.000001}
                      className="input font-mono text-sm py-1.5 pl-5"
                    />
                  </div>
                  <p className="text-xs text-text-muted mt-1">Per share, max ${config.fees?.stocks?.finraTafCap || 8.30}</p>
                </div>
              </div>
              
              {/* Example calculation */}
              <div className="mt-3 p-3 rounded-lg bg-accent-green/10 border border-accent-green/20">
                <p className="text-xs text-accent-green">
                  <strong>Example SELL:</strong> 100 shares @ $150 ($15,000)<br/>
                  SEC: ${(15000 * (config.fees?.stocks?.secFeeRate || 0.0000278)).toFixed(4)} | 
                  FINRA: ${Math.min(100 * (config.fees?.stocks?.finraTafPerShare || 0.000166), config.fees?.stocks?.finraTafCap || 8.30).toFixed(4)} | 
                  <strong> Total: ${(15000 * (config.fees?.stocks?.secFeeRate || 0.0000278) + Math.min(100 * (config.fees?.stocks?.finraTafPerShare || 0.000166), config.fees?.stocks?.finraTafCap || 8.30)).toFixed(4)}</strong>
                </p>
              </div>

              {/* Future: Options & Crypto (collapsed) */}
              <div className="mt-4 p-3 rounded-lg bg-bg-tertiary">
                <p className="text-xs text-text-muted flex items-center gap-2">
                  <span className="text-accent-purple">🔮</span>
                  Options & Crypto fee support coming soon
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Trading Rules */}
        <div>
          <div className="card p-6 mb-6">
            <h2 className="font-display text-lg font-semibold mb-6 flex items-center gap-2">
              <Zap className="w-5 h-5 text-accent-yellow" />
              Trading Rules
            </h2>

            {/* Multi-Select Strategy Selector */}
            <div className="mb-6">
              <label className="block text-text-secondary text-sm mb-2">Trading Strategies <span className="text-text-muted">(select multiple)</span></label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'day', label: '⚡ Day', desc: 'Intraday with full analysis' },
                  { id: 'swing', label: '🌊 Swing', desc: '2-10 day holds' },
                  { id: 'longterm', label: '🏔️ Long', desc: 'Weeks to months' },
                ].map(strat => {
                  const isSelected = config.strategies?.includes(strat.id);
                  const toggleStrategy = () => {
                    const current = config.strategies || [];
                    let newStrategies;
                    if (isSelected) {
                      // Don't allow deselecting if it's the only one
                      if (current.length <= 1) return;
                      newStrategies = current.filter(s => s !== strat.id);
                    } else {
                      newStrategies = [...current, strat.id];
                    }
                    updateConfig('strategies', newStrategies);
                  };
                  return (
                    <button
                      key={strat.id}
                      onClick={toggleStrategy}
                      className={`p-3 rounded-lg border text-center transition-all relative ${
                        isSelected
                          ? 'border-accent-blue bg-accent-blue/10 text-accent-blue'
                          : 'border-border-color hover:border-text-muted'
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute top-1 right-1 w-4 h-4 bg-accent-blue rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      )}
                      <div className="text-lg mb-1">{strat.label}</div>
                      <div className="text-xs text-text-muted">{strat.desc}</div>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-text-muted mt-2">
                Active: {(config.strategies || ['swing']).map(s => s === 'day' ? '⚡Day' : s === 'swing' ? '🌊Swing' : '🏔️Long').join(' + ')}
              </p>
              {config.strategies?.includes('day') && (
                <div className="mt-3 p-3 rounded-lg bg-accent-blue/5 border border-accent-blue/20">
                  <p className="text-xs text-accent-blue">
                    <strong>Day Trading:</strong> Comprehensive analysis including market context, VWAP, volume confirmation, support/resistance, and ATR-based position sizing.
                  </p>
                </div>
              )}
            </div>

            <InputField
              label="Minimum AI Score"
              value={config.minAiScore}
              onChange={(v) => updateConfig('minAiScore', v)}
              min={50}
              max={95}
              step={5}
              suffix="%"
              description="Only trade when AI confidence is above this"
            />

            <InputField
              label="Cooldown Period"
              value={config.cooldownMinutes}
              onChange={(v) => updateConfig('cooldownMinutes', v)}
              min={5}
              max={240}
              step={5}
              suffix="min"
              description="Minutes between trades on same symbol"
            />

            <InputField
              label="Check Interval"
              value={config.checkIntervalSeconds}
              onChange={(v) => updateConfig('checkIntervalSeconds', v)}
              min={30}
              max={600}
              step={30}
              suffix="sec"
              description="How often bot scans for opportunities (30-600 sec)"
            />

            <div className="mb-4">
              <label className="block text-text-secondary text-sm mb-2">Trading Hours Only</label>
              <div className="flex items-center justify-between">
                <span className="text-text-muted text-sm">Only trade during market hours (9:30 AM - 4:00 PM ET)</span>
                <Toggle enabled={config.onlyMarketHours} onChange={(v) => updateConfig('onlyMarketHours', v)} />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-text-secondary text-sm mb-2">Allow Short Selling</label>
              <div className="flex items-center justify-between">
                <span className="text-text-muted text-sm">Enable short positions (advanced)</span>
                <Toggle enabled={config.allowShorts} onChange={(v) => updateConfig('allowShorts', v)} />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-text-secondary text-sm mb-2">Trading Hours</label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <span className="text-xs text-text-muted">Start</span>
                  <input
                    type="time"
                    value={config.startTime}
                    onChange={(e) => updateConfig('startTime', e.target.value)}
                    className="input font-mono"
                  />
                </div>
                <div className="flex-1">
                  <span className="text-xs text-text-muted">End</span>
                  <input
                    type="time"
                    value={config.endTime}
                    onChange={(e) => updateConfig('endTime', e.target.value)}
                    className="input font-mono"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card p-6 mb-6">
            <h2 className="font-display text-lg font-semibold mb-6 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-accent-blue" />
              Trading Days
            </h2>

            <div className="flex flex-wrap gap-2">
              {['mon', 'tue', 'wed', 'thu', 'fri'].map((day) => (
                <button
                  key={day}
                  onClick={() => {
                    const days = config.tradingDays.includes(day)
                      ? config.tradingDays.filter(d => d !== day)
                      : [...config.tradingDays, day];
                    updateConfig('tradingDays', days);
                  }}
                  className={`px-4 py-2 rounded-lg border transition-all capitalize ${
                    config.tradingDays.includes(day)
                      ? 'bg-accent-blue/15 border-accent-blue text-accent-blue'
                      : 'border-border-color text-text-secondary hover:border-text-muted'
                  }`}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={saveConfig}
            disabled={saving}
            className="w-full btn-primary py-4 text-lg flex items-center justify-center gap-2"
          >
            {saving ? (
              <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving...</>
            ) : (
              <><Settings className="w-5 h-5" />Save Settings</>
            )}
          </button>
        </div>
      </div>

      {/* Recent Bot Trades */}
      <div className="card mt-6 overflow-hidden">
        <div className="p-5 border-b border-border-color flex justify-between items-center">
          <h3 className="font-display text-lg font-semibold">Recent Bot Trades</h3>
          <button onClick={clearHistory} className="text-accent-red text-sm hover:underline flex items-center gap-1">
            <Trash2 className="w-4 h-4" />
            Clear History
          </button>
        </div>
        {history.length === 0 ? (
          <div className="p-8 text-center">
            <Bot className="w-12 h-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No bot trades yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Symbol</th>
                  <th>Side</th>
                  <th className="text-right">Qty</th>
                  <th className="text-right">Price</th>
                  <th className="text-right">Value</th>
                  <th className="text-right">AI Score</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((trade, i) => (
                  <tr key={trade.orderId || i}>
                    <td className="text-text-muted text-sm">{new Date(trade.timestamp).toLocaleString()}</td>
                    <td className="font-mono font-semibold">{trade.symbol}</td>
                    <td>
                      <span className={`badge ${trade.side === 'buy' ? 'badge-green' : 'badge-red'}`}>
                        {trade.side.toUpperCase()}
                      </span>
                    </td>
                    <td className="text-right font-mono">{trade.qty}</td>
                    <td className="text-right font-mono">${trade.price?.toFixed(2)}</td>
                    <td className="text-right font-mono">${trade.value?.toFixed(2)}</td>
                    <td className="text-right">
                      <span className={`font-mono ${trade.aiScore >= 70 ? 'text-accent-green' : 'text-text-secondary'}`}>
                        {trade.aiScore}%
                      </span>
                    </td>
                    <td>
                      {trade.status === 'filled' ? (
                        <CheckCircle className="w-4 h-4 text-accent-green" />
                      ) : (
                        <XCircle className="w-4 h-4 text-accent-red" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Trading Strategy Info */}
      <div className="mt-6 card p-6">
        <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
          <Brain className="w-5 h-5 text-accent-blue" />
          How the Bot Trades {config.strategies?.includes('day') && <span className="text-sm font-normal text-accent-blue">(Day Trading Mode)</span>}
        </h3>
        
        {config.strategies?.includes('day') ? (
          // Day Trading specific info
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div>
                <h4 className="font-semibold text-accent-green mb-2">🟢 BUY Checklist (All Required)</h4>
                <ul className="space-y-1 text-text-secondary">
                  <li>✅ <strong>Market Direction:</strong> SPY/QQQ trending favorably</li>
                  <li>✅ <strong>Price Level:</strong> Near support or above VWAP</li>
                  <li>✅ <strong>Volume Confirmation:</strong> RVOL &gt; 1.0x</li>
                  <li>✅ <strong>Risk/Reward:</strong> At least 2:1 ratio</li>
                  <li>✅ <strong>Trend Alignment:</strong> Stock + market + sector aligned</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-accent-red mb-2">🔴 SELL Triggers</h4>
                <ul className="space-y-1 text-text-secondary">
                  <li>• <strong>Stop Loss:</strong> ATR-based or -{config.stopLossPercent}%</li>
                  <li>• <strong>Take Profit:</strong> At resistance or +{config.takeProfitPercent}%</li>
                  <li>• <strong>VWAP Breakdown:</strong> Price breaks VWAP with volume</li>
                  <li>• <strong>Market Shift:</strong> SPY/QQQ turn bearish</li>
                  <li>• <strong>Score Drop:</strong> AI score falls below 40%</li>
                </ul>
              </div>
            </div>
            
            <div className="p-4 bg-bg-tertiary rounded-lg">
              <h4 className="font-semibold mb-3 text-text-primary">📊 Analysis Components</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                <div className="text-center p-2 bg-bg-card rounded">
                  <div className="text-accent-blue font-semibold">VWAP</div>
                  <div className="text-text-muted">Fair Value</div>
                </div>
                <div className="text-center p-2 bg-bg-card rounded">
                  <div className="text-accent-green font-semibold">S&R</div>
                  <div className="text-text-muted">Key Levels</div>
                </div>
                <div className="text-center p-2 bg-bg-card rounded">
                  <div className="text-accent-purple font-semibold">RVOL</div>
                  <div className="text-text-muted">Volume Ratio</div>
                </div>
                <div className="text-center p-2 bg-bg-card rounded">
                  <div className="text-accent-yellow font-semibold">ATR</div>
                  <div className="text-text-muted">Volatility</div>
                </div>
                <div className="text-center p-2 bg-bg-card rounded">
                  <div className="text-accent-red font-semibold">SPY</div>
                  <div className="text-text-muted">Market Bias</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Standard swing/longterm info
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-semibold text-accent-green mb-2">🟢 BUY Conditions</h4>
              <ul className="space-y-1 text-text-secondary">
                <li>• AI Score above minimum threshold</li>
                <li>• Not already holding the stock</li>
                <li>• Under max position limit</li>
                <li>• Under daily trade limit</li>
                <li>• Within market hours</li>
                <li>• Symbol not on cooldown</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-accent-red mb-2">🔴 SELL Conditions</h4>
              <ul className="space-y-1 text-text-secondary">
                <li>• <strong>Stop Loss:</strong> Price drops below -{config.stopLossPercent}%</li>
                <li>• <strong>Take Profit:</strong> Price rises above +{config.takeProfitPercent}%</li>
                <li>• <strong>AI Signal:</strong> AI score drops below 35%</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* WSB Integration Section */}
      <div className="mt-6 card p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-orange-500" />
            WallStreetBets Integration
            <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded-full">BETA</span>
          </h3>
          <div className="flex items-center gap-3">
            <button 
              onClick={fetchWSBData} 
              disabled={wsbLoading}
              className="btn-secondary p-2 rounded-lg"
            >
              <RefreshCw className={`w-4 h-4 ${wsbLoading ? 'animate-spin' : ''}`} />
            </button>
            <a 
              href="https://www.reddit.com/r/wallstreetbets/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-secondary p-2 rounded-lg"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* WSB Enable Toggle */}
        <div className="mb-6 p-4 rounded-lg bg-bg-tertiary">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold">Enable WSB Signals</h4>
              <p className="text-xs text-text-muted mt-1">Allow bot to trade based on r/wallstreetbets sentiment</p>
            </div>
            <Toggle enabled={config?.wsbEnabled} onChange={(v) => updateConfig('wsbEnabled', v)} />
          </div>

          {config?.wsbEnabled && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-border-color">
              <div>
                <label className="block text-xs text-text-muted mb-1">Min Mentions</label>
                <input 
                  type="number" 
                  value={config.wsbMinMentions || 3}
                  onChange={(e) => updateConfig('wsbMinMentions', parseInt(e.target.value))}
                  min={1} max={20}
                  className="input text-sm py-1.5"
                />
              </div>
              <div>
                <label className="block text-xs text-text-muted mb-1">Min Confidence</label>
                <input 
                  type="number" 
                  value={config.wsbMinConfidence || 50}
                  onChange={(e) => updateConfig('wsbMinConfidence', parseInt(e.target.value))}
                  min={20} max={90}
                  className="input text-sm py-1.5"
                />
              </div>
              <div>
                <label className="block text-xs text-text-muted mb-1">Max Risk Score</label>
                <input 
                  type="number" 
                  value={config.wsbMaxRisk || 70}
                  onChange={(e) => updateConfig('wsbMaxRisk', parseInt(e.target.value))}
                  min={30} max={100}
                  className="input text-sm py-1.5"
                />
              </div>
              <div>
                <label className="block text-xs text-text-muted mb-1">Position Size %</label>
                <input 
                  type="number" 
                  value={config.wsbTradePercent || 25}
                  onChange={(e) => updateConfig('wsbTradePercent', parseInt(e.target.value))}
                  min={10} max={100}
                  className="input text-sm py-1.5"
                />
              </div>
            </div>
          )}
        </div>

        {/* WSB Trending Tickers */}
        <div>
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <Flame className="w-4 h-4 text-orange-500" />
            Trending on WSB
          </h4>
          
          {wsbLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="p-3 bg-bg-tertiary rounded-lg animate-pulse">
                  <div className="h-4 bg-bg-card rounded w-12 mb-2" />
                  <div className="h-3 bg-bg-card rounded w-20" />
                </div>
              ))}
            </div>
          ) : wsbTrending.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {wsbTrending.map(ticker => (
                <div 
                  key={ticker.ticker} 
                  className={`p-3 rounded-lg border transition-all hover:scale-105 ${
                    ticker.sentimentLabel === 'bullish' 
                      ? 'bg-accent-green/5 border-accent-green/30' 
                      : ticker.sentimentLabel === 'bearish'
                      ? 'bg-accent-red/5 border-accent-red/30'
                      : 'bg-bg-tertiary border-border-color'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold font-mono">${ticker.ticker}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      ticker.riskLabel === 'high' ? 'bg-accent-red/20 text-accent-red' :
                      ticker.riskLabel === 'medium' ? 'bg-accent-yellow/20 text-accent-yellow' :
                      'bg-accent-green/20 text-accent-green'
                    }`}>
                      {ticker.riskLabel}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-text-muted">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {ticker.mentions}
                    </span>
                    <span className={`font-semibold ${
                      ticker.sentiment >= 60 ? 'text-accent-green' : 
                      ticker.sentiment <= 40 ? 'text-accent-red' : 'text-text-secondary'
                    }`}>
                      {ticker.sentiment}%
                    </span>
                  </div>
                  {ticker.signal.action !== 'none' && ticker.signal.action !== 'insufficient_data' && (
                    <div className={`mt-2 text-xs px-2 py-1 rounded text-center ${
                      ticker.signal.action.includes('buy') ? 'bg-accent-green/20 text-accent-green' :
                      ticker.signal.action === 'avoid' ? 'bg-accent-red/20 text-accent-red' :
                      'bg-accent-blue/20 text-accent-blue'
                    }`}>
                      {ticker.signal.action.replace(/_/g, ' ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-text-muted">
              <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No trending tickers found</p>
              <button onClick={fetchWSBData} className="text-accent-blue text-sm mt-2">Refresh</button>
            </div>
          )}
        </div>

        {/* WSB Warning */}
        <div className="mt-4 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
          <p className="text-xs text-orange-400">
            <strong>Warning:</strong> WSB signals are based on social media sentiment and carry significantly higher risk. 
            Position sizes are automatically reduced to {config?.wsbTradePercent || 25}% of normal trades. 
            Only enable if you understand and accept the risks of momentum/meme trading.
          </p>
        </div>
      </div>

      {/* Options Trading - Managed in Options Dashboard */}
      <div className="card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Layers className="w-6 h-6 text-accent-orange" />
            <div>
              <h3 className="font-display text-lg font-semibold">Options Trading</h3>
              <p className="text-text-muted text-sm">Configure automated options trading in the Options dashboard</p>
            </div>
          </div>
          <Link to="/options" className="btn-primary flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Open Options Dashboard
          </Link>
        </div>
      </div>

      {/* ============ SCALING / MARTINGALE SIZER (Phase 4) ============ */}
      <div className="card p-6">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="w-6 h-6 text-accent-purple" />
          <div>
            <h3 className="font-display text-lg font-semibold">Position Scaling</h3>
            <p className="text-text-muted text-sm">Anti-martingale, martingale, or scale-in winners</p>
          </div>
          <div className="ml-auto">
            <Toggle 
              enabled={config?.scalingSizer?.enabled || false} 
              onChange={(val) => updateConfig('scalingSizer', { ...config?.scalingSizer, enabled: val })} 
            />
          </div>
        </div>

        {config?.scalingSizer?.enabled && (
          <div className="space-y-4">
            <div>
              <label className="block text-text-secondary text-sm mb-2">Sizing Mode</label>
              <div className="flex gap-2">
                {[
                  { id: 'anti_martingale', label: 'Anti-Martingale', desc: 'Increase after wins, decrease after losses (safest)', color: 'green' },
                  { id: 'martingale', label: 'Martingale', desc: 'Increase after losses to recover (risky)', color: 'red' },
                  { id: 'scale_in_winners', label: 'Scale-In Winners', desc: 'Add to winning positions at profit thresholds', color: 'blue' },
                ].map(mode => (
                  <button
                    key={mode.id}
                    onClick={() => updateConfig('scalingSizer', { ...config?.scalingSizer, mode: mode.id })}
                    className={`flex-1 p-3 rounded-xl text-center transition-all ${
                      config?.scalingSizer?.mode === mode.id 
                        ? `bg-accent-${mode.color}/15 border border-accent-${mode.color}/30 text-accent-${mode.color}`
                        : 'bg-bg-tertiary border border-border-color text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    <div className="text-sm font-semibold">{mode.label}</div>
                    <div className="text-xs mt-1 text-text-muted">{mode.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {config?.scalingSizer?.mode === 'anti_martingale' && (
              <div className="grid grid-cols-2 gap-4 bg-bg-tertiary rounded-xl p-4">
                <InputField label="Win Multiplier" value={config?.scalingSizer?.antiMartingale?.winMultiplier || 1.2} onChange={(v) => updateConfig('scalingSizer', { ...config?.scalingSizer, antiMartingale: { ...config?.scalingSizer?.antiMartingale, winMultiplier: v } })} min={1.0} max={2.0} step={0.1} description="Size increase after each win" />
                <InputField label="Loss Multiplier" value={config?.scalingSizer?.antiMartingale?.lossMultiplier || 0.75} onChange={(v) => updateConfig('scalingSizer', { ...config?.scalingSizer, antiMartingale: { ...config?.scalingSizer?.antiMartingale, lossMultiplier: v } })} min={0.3} max={1.0} step={0.05} description="Size decrease after each loss" />
                <InputField label="Max Multiplier" value={config?.scalingSizer?.antiMartingale?.maxMultiplier || 2.0} onChange={(v) => updateConfig('scalingSizer', { ...config?.scalingSizer, antiMartingale: { ...config?.scalingSizer?.antiMartingale, maxMultiplier: v } })} min={1.5} max={3.0} step={0.1} description="Cap on size increase" />
                <InputField label="Min Multiplier" value={config?.scalingSizer?.antiMartingale?.minMultiplier || 0.4} onChange={(v) => updateConfig('scalingSizer', { ...config?.scalingSizer, antiMartingale: { ...config?.scalingSizer?.antiMartingale, minMultiplier: v } })} min={0.2} max={0.8} step={0.05} description="Floor on size decrease" />
              </div>
            )}

            {config?.scalingSizer?.mode === 'martingale' && (
              <div className="grid grid-cols-2 gap-4 bg-bg-tertiary rounded-xl p-4">
                <InputField label="Multiplier" value={config?.scalingSizer?.martingale?.multiplier || 1.5} onChange={(v) => updateConfig('scalingSizer', { ...config?.scalingSizer, martingale: { ...config?.scalingSizer?.martingale, multiplier: v } })} min={1.1} max={3.0} step={0.1} description="Size increase after each loss" />
                <InputField label="Max Rounds" value={config?.scalingSizer?.martingale?.maxRounds || 3} onChange={(v) => updateConfig('scalingSizer', { ...config?.scalingSizer, martingale: { ...config?.scalingSizer?.martingale, maxRounds: v } })} min={1} max={5} step={1} description="Max consecutive increases" />
                <div className="col-span-2 p-3 bg-accent-red/10 border border-accent-red/20 rounded-lg">
                  <p className="text-xs text-accent-red"><strong>Warning:</strong> Martingale increases position size after losses. This can amplify losses rapidly. Use with extreme caution and strict daily loss limits.</p>
                </div>
              </div>
            )}

            {config?.scalingSizer?.mode === 'scale_in_winners' && (
              <div className="bg-bg-tertiary rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-text-secondary">Scale-In Enabled</span>
                  <Toggle 
                    enabled={config?.scalingSizer?.scaleIn?.enabled || false} 
                    onChange={(val) => updateConfig('scalingSizer', { ...config?.scalingSizer, scaleIn: { ...config?.scalingSizer?.scaleIn, enabled: val } })} 
                  />
                </div>
                <p className="text-xs text-text-muted mb-3">Adds to winning positions at 2%, 5%, 8% profit thresholds. Each addition is 50%, 30%, 20% of original size.</p>
                <InputField label="Max Scale-Ins" value={config?.scalingSizer?.scaleIn?.maxScaleIns || 3} onChange={(v) => updateConfig('scalingSizer', { ...config?.scalingSizer, scaleIn: { ...config?.scalingSizer?.scaleIn, maxScaleIns: v } })} min={1} max={5} step={1} description="Max additions per position" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ============ PROP FIRM GUARD (Phase 5) ============ */}
      <div className="card p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-6 h-6 text-accent-yellow" />
          <div>
            <h3 className="font-display text-lg font-semibold">Prop Firm Guard</h3>
            <p className="text-text-muted text-sm">Drawdown enforcement with auto-flatten protection</p>
          </div>
          <div className="ml-auto">
            <Toggle 
              enabled={config?.propFirmGuard?.enabled || false} 
              onChange={(val) => updateConfig('propFirmGuard', { ...config?.propFirmGuard, enabled: val })} 
            />
          </div>
        </div>

        {config?.propFirmGuard?.enabled && (
          <div className="space-y-4">
            {/* Preset Selection */}
            <div>
              <label className="block text-text-secondary text-sm mb-2">Preset</label>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { id: 'ftmo', label: 'FTMO', dd: '5% / 10%' },
                  { id: 'topstep', label: 'TopStep', dd: '2% / 3.5%' },
                  { id: 'conservative', label: 'Conservative', dd: '2% / 6%' },
                  { id: 'moderate', label: 'Moderate', dd: '3% / 8%' },
                  { id: 'aggressive', label: 'Aggressive', dd: '5% / 15%' },
                ].map(preset => (
                  <button
                    key={preset.id}
                    onClick={() => {
                      const presets = { ftmo: { maxDailyDrawdownPct: 5, maxTotalDrawdownPct: 10, useTrailingDrawdown: false, autoFlatten: true, autoShutdown: true }, topstep: { maxDailyDrawdownPct: 2, maxTotalDrawdownPct: 3.5, useTrailingDrawdown: true, autoFlatten: true, autoShutdown: true }, conservative: { maxDailyDrawdownPct: 2, maxTotalDrawdownPct: 6, useTrailingDrawdown: true, autoFlatten: false }, moderate: { maxDailyDrawdownPct: 3, maxTotalDrawdownPct: 8, useTrailingDrawdown: true, autoFlatten: true }, aggressive: { maxDailyDrawdownPct: 5, maxTotalDrawdownPct: 15, useTrailingDrawdown: false, autoFlatten: true } };
                      updateConfig('propFirmGuard', { ...config?.propFirmGuard, ...presets[preset.id], preset: preset.id });
                    }}
                    className={`p-2 rounded-lg text-center transition-all ${
                      config?.propFirmGuard?.preset === preset.id
                        ? 'bg-accent-yellow/15 border border-accent-yellow/30 text-accent-yellow'
                        : 'bg-bg-tertiary border border-border-color text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    <div className="text-xs font-semibold">{preset.label}</div>
                    <div className="text-[10px] text-text-muted mt-0.5">{preset.dd}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Thresholds */}
            <div className="grid grid-cols-2 gap-4 bg-bg-tertiary rounded-xl p-4">
              <InputField label="Max Daily Drawdown %" value={config?.propFirmGuard?.maxDailyDrawdownPct || 3} onChange={(v) => updateConfig('propFirmGuard', { ...config?.propFirmGuard, maxDailyDrawdownPct: v, preset: 'custom' })} min={0.5} max={10} step={0.5} description="Max allowed daily loss" />
              <InputField label="Max Total Drawdown %" value={config?.propFirmGuard?.maxTotalDrawdownPct || 8} onChange={(v) => updateConfig('propFirmGuard', { ...config?.propFirmGuard, maxTotalDrawdownPct: v, preset: 'custom' })} min={1} max={25} step={0.5} description="Max allowed total/trailing loss" />
            </div>

            {/* Toggle Options */}
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center justify-between bg-bg-tertiary rounded-lg p-3">
                <div>
                  <div className="text-sm">Trailing DD</div>
                  <div className="text-xs text-text-muted">Track from HWM</div>
                </div>
                <Toggle enabled={config?.propFirmGuard?.useTrailingDrawdown !== false} onChange={(val) => updateConfig('propFirmGuard', { ...config?.propFirmGuard, useTrailingDrawdown: val })} />
              </div>
              <div className="flex items-center justify-between bg-bg-tertiary rounded-lg p-3">
                <div>
                  <div className="text-sm">Auto Flatten</div>
                  <div className="text-xs text-text-muted">Close all at limit</div>
                </div>
                <Toggle enabled={config?.propFirmGuard?.autoFlatten !== false} onChange={(val) => updateConfig('propFirmGuard', { ...config?.propFirmGuard, autoFlatten: val })} />
              </div>
              <div className="flex items-center justify-between bg-bg-tertiary rounded-lg p-3">
                <div>
                  <div className="text-sm">Auto Shutdown</div>
                  <div className="text-xs text-text-muted">Stop bot after flatten</div>
                </div>
                <Toggle enabled={config?.propFirmGuard?.autoShutdown || false} onChange={(val) => updateConfig('propFirmGuard', { ...config?.propFirmGuard, autoShutdown: val })} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Warning */}
      <div className="mt-6 p-4 rounded-xl bg-accent-yellow/10 border border-accent-yellow/20">
        <p className="text-sm text-accent-yellow flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>
            <strong>Warning:</strong> Automated trading involves significant risk. The bot makes decisions based on 
            AI analysis which may not always be accurate. Only use funds you can afford to lose. This is a paper 
            trading bot - no real money is involved.
          </span>
        </p>
      </div>
      </div>
    </div>
  );
};

export default Admin;
